/**
 * Settings → Organization → API access. Org admins create keys (shown once), see where each key
 * has been used, revoke keys, and see registered webhooks. Docs live at /api-docs.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControlLabel, IconButton, InputAdornment, MenuItem, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import KeyIcon from '@mui/icons-material/VpnKey';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import WebhookIcon from '@mui/icons-material/Webhook';
import RefreshIcon from '@mui/icons-material/Refresh';
import { API_SCOPES, READ_ONLY_SCOPES, createApiKey, listApiKeys, listApiLog, listWebhooks, revokeApiKey } from '../../services/apiKeyService';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';

const fmt = (ts) => (ts?.toDate ? ts.toDate().toLocaleString() : ts ? new Date(ts).toLocaleString() : '—');

function CreateKeyDialog({ open, onClose, onCreate, busy }) {
  const [label, setLabel] = useState('');
  const [scopes, setScopes] = useState(READ_ONLY_SCOPES);
  const [expires, setExpires] = useState('');
  const toggle = (k) => setScopes((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New API key</DialogTitle>
      <DialogContent>
        <TextField autoFocus fullWidth margin="dense" label="What is this key for?" placeholder="e.g. Power BI dashboard, CMMS integration" value={label} onChange={(e) => setLabel(e.target.value)} inputProps={{ maxLength: 80 }} />
        <TextField select fullWidth margin="dense" label="Expires" value={expires} onChange={(e) => setExpires(e.target.value)} helperText="Keys can be revoked at any time regardless of expiry.">
          <MenuItem value="">Never</MenuItem>
          <MenuItem value="30">In 30 days</MenuItem>
          <MenuItem value="90">In 90 days</MenuItem>
          <MenuItem value="365">In 1 year</MenuItem>
        </TextField>
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Permissions</Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Button size="small" onClick={() => setScopes(READ_ONLY_SCOPES)}>Read only</Button>
          <Button size="small" onClick={() => setScopes(API_SCOPES.map((s) => s.key))}>Everything</Button>
          <Button size="small" onClick={() => setScopes([])}>None</Button>
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.5 }}>
          {API_SCOPES.map((s) => (
            <FormControlLabel
              key={s.key}
              control={<Checkbox size="small" checked={scopes.includes(s.key)} onChange={() => toggle(s.key)} />}
              label={<Box><Typography variant="body2">{s.label}</Typography><Typography variant="caption" color="text.secondary">{s.help}</Typography></Box>}
              sx={{ alignItems: 'flex-start', m: 0 }}
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" disabled={busy || scopes.length === 0} onClick={() => onCreate({ label, scopes, expiresInDays: expires ? Number(expires) : null })}>
          {busy ? 'Creating…' : 'Create key'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ShowKeyDialog({ created, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => { try { await navigator.clipboard.writeText(created.key); setCopied(true); } catch { /* fine */ } };
  return (
    <Dialog open={Boolean(created)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Copy your new key</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>This is the only time the full key is shown. Store it somewhere safe — if it is lost, revoke it and create a new one.</Alert>
        <TextField
          fullWidth
          value={created?.key || ''}
          InputProps={{ readOnly: true, sx: { fontFamily: '"Roboto Mono", monospace', fontSize: '0.9rem' }, endAdornment: (<InputAdornment position="end"><IconButton onClick={copy} aria-label="Copy key"><ContentCopyIcon /></IconButton></InputAdornment>) }}
          onFocus={(e) => e.target.select()}
        />
        {copied && <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5 }}>Copied.</Typography>}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Try it:</Typography>
        <Box component="pre" sx={{ m: 0, mt: 0.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, fontSize: '0.78rem', overflowX: 'auto' }}>
{`curl -H "Authorization: Bearer ${created?.key || 'rsk_live_…'}" \\
  ${window.location.origin}/api/v1/me`}
        </Box>
      </DialogContent>
      <DialogActions><Button variant="contained" onClick={onClose}>I have saved it</Button></DialogActions>
    </Dialog>
  );
}

export function ApiAccessSection({ organizationId }) {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const { getUserRole } = useUser();
  // Org admins and PPI only — the same gate as the Document Library section.
  const canManage = isSuperAdmin() || getUserRole(organizationId)?.role === 'org_admin';
  const [keys, setKeys] = useState([]);
  const [hooks, setHooks] = useState([]);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [confirmRevoke, setConfirmRevoke] = useState(null);

  const load = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    setError(null);
    try {
      const [k, h, l] = await Promise.all([listApiKeys(organizationId), listWebhooks(organizationId), listApiLog(organizationId, { limit: 25 }).catch(() => [])]);
      setKeys(k); setHooks(h); setLog(l);
    } catch (e) {
      setError(/permission/i.test(e?.message || '') ? 'API access is not enabled on this environment yet (Firestore rules and the hugoApi functions need to be deployed).' : e?.message || 'Could not load API keys.');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);
  useEffect(() => { load(); }, [load]);

  const active = useMemo(() => keys.filter((k) => !k.revokedAt), [keys]);
  const revoked = useMemo(() => keys.filter((k) => k.revokedAt), [keys]);
  if (!canManage) return null;

  const onCreate = async (data) => {
    setBusy(true);
    try {
      const res = await createApiKey({ organizationId, ...data });
      setCreateOpen(false);
      setCreated(res);
      await load();
    } catch (e) {
      setError(e?.message || 'Could not create the key.');
    } finally {
      setBusy(false);
    }
  };
  const onRevoke = async () => {
    const k = confirmRevoke;
    setConfirmRevoke(null);
    try { await revokeApiKey(k.id); await load(); } catch (e) { setError(e?.message || 'Could not revoke the key.'); }
  };

  return (
    <Card sx={{ mt: 3 }} id="api-access">
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
          <KeyIcon color="primary" />
          <Typography variant="h6" sx={{ flex: 1, minWidth: 160 }}>API access</Typography>
          <Tooltip title="Reload"><IconButton size="small" onClick={load} disabled={loading}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
          <Button size="small" startIcon={<MenuBookIcon />} onClick={() => navigate('/api-docs')} sx={{ textTransform: 'none', fontWeight: 700 }}>API docs</Button>
          <Button size="small" variant="contained" onClick={() => setCreateOpen(true)} sx={{ textTransform: 'none', fontWeight: 700 }}>New key</Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
          Connect your own systems to REPSit.ai — BI dashboards, a CMMS, an incident system, Power Automate, n8n. Each key belongs to this organization,
          carries only the permissions you tick, and every call is logged below.
        </Typography>
        {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
        ) : active.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">No API keys yet. Create one to get started — read-only is a safe first step.</Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Key</TableCell>
                  <TableCell>Permissions</TableCell>
                  <TableCell>Last used</TableCell>
                  <TableCell align="right">Calls</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {active.map((k) => (
                  <TableRow key={k.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>{k.label || 'Untitled key'}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: '"Roboto Mono", monospace' }}>{k.prefix}…</Typography>
                      {k.expiresAt && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Expires {fmt(k.expiresAt)}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                        {(k.scopes || []).map((s) => <Chip key={s} size="small" label={s} variant="outlined" sx={{ fontSize: '0.68rem', height: 20 }} />)}
                      </Stack>
                    </TableCell>
                    <TableCell><Typography variant="body2">{k.lastUsedAt ? fmt(k.lastUsedAt) : 'Never'}</Typography></TableCell>
                    <TableCell align="right"><Typography variant="body2">{k.useCount || 0}</Typography></TableCell>
                    <TableCell align="right">
                      <Tooltip title="Revoke this key"><IconButton size="small" color="error" onClick={() => setConfirmRevoke(k)} aria-label={`Revoke ${k.label}`}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {revoked.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {revoked.length} revoked key{revoked.length === 1 ? '' : 's'} kept for the audit trail.
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <WebhookIcon fontSize="small" color="action" />
          <Typography variant="subtitle2">Webhooks</Typography>
        </Box>
        {hooks.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            None registered. Webhooks are created through the API (<code>POST /v1/webhooks</code>) with a key that has the <code>webhooks:manage</code> permission — see the docs.
          </Typography>
        ) : (
          <Stack spacing={0.75}>
            {hooks.map((h) => (
              <Box key={h.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={h.active === false ? 'Paused' : 'Active'} color={h.active === false ? 'default' : 'success'} variant="outlined" sx={{ height: 20, fontSize: '0.68rem' }} />
                <Typography variant="body2" sx={{ fontFamily: '"Roboto Mono", monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>{h.url}</Typography>
                <Typography variant="caption" color="text.secondary">{(h.events || []).join(', ') || 'all events'} · last {h.lastDeliveryStatus ?? '—'}</Typography>
              </Box>
            ))}
          </Stack>
        )}

        {log.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Recent API calls</Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead><TableRow><TableCell>When</TableCell><TableCell>Request</TableCell><TableCell align="right">Status</TableCell><TableCell align="right">ms</TableCell></TableRow></TableHead>
                <TableBody>
                  {log.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell><Typography variant="caption">{fmt(l.at)}</Typography></TableCell>
                      <TableCell><Typography variant="caption" sx={{ fontFamily: '"Roboto Mono", monospace' }}>{l.method} {l.path}</Typography></TableCell>
                      <TableCell align="right"><Chip size="small" label={l.status} color={l.status < 400 ? 'success' : l.status < 500 ? 'warning' : 'error'} variant="outlined" sx={{ height: 20, fontSize: '0.68rem' }} /></TableCell>
                      <TableCell align="right"><Typography variant="caption">{l.ms}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </CardContent>

      <CreateKeyDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreate={onCreate} busy={busy} />
      <ShowKeyDialog created={created} onClose={() => setCreated(null)} />
      <Dialog open={Boolean(confirmRevoke)} onClose={() => setConfirmRevoke(null)}>
        <DialogTitle>Revoke “{confirmRevoke?.label || 'this key'}”?</DialogTitle>
        <DialogContent><Typography variant="body2">Anything using this key stops working immediately. This cannot be undone.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRevoke(null)}>Keep it</Button>
          <Button color="error" variant="contained" onClick={onRevoke}>Revoke</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

export default ApiAccessSection;
